<template>
  <div class="audio-player">
    <div class="audio-player__controls">
      <button
        v-if="hasPlaylist"
        type="button"
        class="audio-player__skip"
        aria-label="Previous track"
        @click="audioPlayerStore.selectDemo(demoIndex - 1)">
        <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6 5h2v14H6zM19 5v14L9 12z" fill="currentColor" /></svg>
      </button>
      <product-play
        :class="['audio-player__play-button', { 'is-disabled': elementPrefix === 'footer' && audioPlayerStore.audioErrorMessage, 'is-cdn': isCdnDemo }]"
        :reduced-size="elementPrefix === 'footer'"
        :product-details="productDetails"
        :show-text="showText"
        :soundcloud-id="trackId"
        :hide-content="elementPrefix === 'product' && (!trackId || (lastCheckedTrackId === trackId && isTrackAvailable === false))"
        :on-toggle="elementPrefix === 'product' ? handleProductToggle : undefined" />
      <button
        v-if="hasPlaylist"
        type="button"
        class="audio-player__skip"
        aria-label="Next track"
        @click="audioPlayerStore.selectDemo(demoIndex + 1)">
        <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M16 5h2v14h-2zM5 5v14l10-7z" fill="currentColor" /></svg>
      </button>
    </div>
    <template v-if="elementPrefix === 'footer'">
      <div v-if="audioSrc" :id="playerElementId" :class="['audio-player__waveform', { 'is-ready': waveIsReady }]" />
      <div v-else-if="!isLoading && audioPlayerStore.audioErrorMessage" class="audio-player__error">
        {{ audioPlayerStore.audioErrorMessage }}
      </div>
      <div v-if="audioSrc && showTrackLength && !audioPlayerStore.audioErrorMessage" class="audio-player__time">
        <span v-if="hasPlaylist" class="audio-player__track">Track {{ demoIndex + 1 }}/{{ demoCount }}</span>
        <span>{{ playbackTime }} / {{ duration }}</span>
      </div>
      <label v-if="audioSrc && !audioPlayerStore.audioErrorMessage" class="audio-player__volume">
        <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M3 9v6h4l5 5V4L7 9zm13.5 3A4.5 4.5 0 0 0 14 7.97v8.05A4.5 4.5 0 0 0 16.5 12z" fill="currentColor" /></svg>
        <input
          type="range"
          min="0"
          max="1"
          step="0.01"
          aria-label="Volume"
          :value="audioPlayerStore.volume"
          @input="audioPlayerStore.setVolume($event.target.value)">
      </label>
    </template>
  </div>
</template>

<script>
import { useAudioPlayerStore } from "~/stores/audioplayer";
import { formatTime, getAudioKey } from "~/utils/audioplayer";
import { attachMedia, prepareMedia } from "~/utils/audioGain";
import { nextTick } from 'vue'
import WaveSurfer from 'wavesurfer.js';

export default {
  props: {
    elementPrefix: {
      type: String,
      required: true,
    },
    trackId: String,
    autoPlay: Boolean,
    showTrackLength: {
      type: Boolean,
      default: true,
    },
    productDetails: Object,
    showText: Boolean,
  },
  setup() {
    const audioPlayerStore = useAudioPlayerStore()

    return { audioPlayerStore }
  },
  data() {
    return {
      audioSrc: '',
      audioSource: null,
      displayWaveSurfer: null,
      playerElementId: `${this.elementPrefix}-waveformBar`,
      playbackTime: '0:00',
      duration: '0:00',
      waveIsReady: false,
      isTrackAvailable: null,
      lastCheckedTrackId: null,
      isLoading: this.elementPrefix === 'footer',
      isFetchingTrack: false, // Lock to prevent simultaneous loads
    }
  },
  mounted() {
    if (this.elementPrefix === 'footer') {
      this.audioPlayerStore.loadVolume()
    }
    if (this.elementPrefix === 'footer' && this.trackId) {
      this.fetchTrack();
    }
    if (this.elementPrefix === 'product' && this.trackId) {
      this.checkAvailability();
    }
  },
  beforeDestroy() {
    if (this.displayWaveSurfer) {
      this.displayWaveSurfer.destroy()
    }
  },
  computed: {
    // Demo served from our own CDN instead of the SoundCloud proxy
    isCdnDemo() {
      return !!this.productDetails?.audioDemos?.length
    },
    loadedWaveSurfer() {
      return this.audioPlayerStore.loadedWaveSurfer;
    },
    demoCount() {
      return this.productDetails?.audioDemos?.length ?? 0
    },
    demoIndex() {
      return this.audioPlayerStore.demoIndex
    },
    // Skip buttons and track number only in the footer and only for more than one demo
    hasPlaylist() {
      return this.elementPrefix === 'footer' && this.demoCount > 1
    },
  },
  watch: {
    trackId(newId, oldId) {
      if (newId && newId !== oldId) {
        if (this.elementPrefix === 'footer') {
          // fetchTrack has its own locking mechanism, safe to call
          this.fetchTrack();
        } else if (this.elementPrefix === 'product') {
          this.isTrackAvailable = null;
          this.checkAvailability();
        }
      }
    },
    '$route.fullPath'(to, from) {
      if (this.elementPrefix === 'product') {
        this.isTrackAvailable = null
        this.lastCheckedTrackId = null
        if (this.trackId) {
          this.checkAvailability()
        }
      }
    }
  },
  methods: {
    async checkAvailability() {
      const currentId = this.trackId
      if (this.productDetails?.audioDemos?.length) {
        // CDN demos need no availability probe
        this.isTrackAvailable = true
        this.lastCheckedTrackId = currentId
        return
      }
      const trackType = this.productDetails?.soundcloudIdType ?? 'track'
      const available = await this.audioPlayerStore.checkTrackAvailability(currentId, trackType)
      if (this.trackId === currentId) {
        this.isTrackAvailable = available
        this.lastCheckedTrackId = currentId
      }
    },
    handleProductToggle() {
      this.isTrackAvailable = null
      this.lastCheckedTrackId = null

      // If a different track needs to be loaded
      if (! this.audioPlayerStore?.loadedProductDetails
        || (getAudioKey(this.audioPlayerStore.loadedProductDetails) !== getAudioKey(this.productDetails))) {

        // Stop any currently playing audio first
        if (this.audioPlayerStore.loadedWaveSurfer) {
          this.audioPlayerStore.stopPlayback(false, false);
        }

        this.audioPlayerStore.loadFooterAudio(this.productDetails, true)
        return
      }

      // Same track - just toggle play/pause
      this.audioPlayerStore.togglePlay()
    },
    async fetchTrack() {
      if (this.elementPrefix !== 'footer') {
        return;
      }

      // Prevent simultaneous loads - if already fetching, ignore this request
      if (this.isFetchingTrack) {
        console.log('[AudioPlayer] Already fetching track, ignoring duplicate request');
        return;
      }

      // Set lock
      const requestedTrackId = this.trackId;
      this.isFetchingTrack = true;
      this.isLoading = true;

      // Clean up any existing WaveSurfer instance before loading new track
      if (this.displayWaveSurfer) {
        try {
          this.displayWaveSurfer.destroy();
        } catch (e) {
          console.warn('[AudioPlayer] Error destroying previous WaveSurfer:', e);
        }
        this.displayWaveSurfer = null;
        this.waveIsReady = false;
      }
      // A new track starts at the beginning, also when it is loaded paused
      this.playbackTime = '0:00';

      try {
        this.audioSource = await this.audioPlayerStore.fetchTrackWithHandling(this.trackId, this.productDetails);
        this.audioSrc = this.audioSource?.url ?? '';

        if (this.audioSrc) {
          await this.initWaveSurfer();
        } else {
          this.audioSrc = '';
        }
      } catch (error) {
        console.error('[AudioPlayer] Error fetching track:', error);
        this.audioSrc = '';
      } finally {
        // Release lock
        this.isFetchingTrack = false;
        this.isLoading = false;
      }

      // A different track was requested while the lock was held
      if (this.trackId && this.trackId !== requestedTrackId) {
        this.fetchTrack();
      }
    },
    async initWaveSurfer() {
      if (this.autoPlay) {
        this.audioPlayerStore.destroyLoadedWaveSurfer()
      }

      await nextTick()

      const { peaks, duration } = this.audioSource ?? {}
      const hasPeaks = !!(peaks && duration)

      this.displayWaveSurfer = WaveSurfer.create({
        container: `#${this.playerElementId}`,
        waveColor: '#000000',
        progressColor: '#ef860c',
        height: 42,
        barWidth: 3,
        barGap: 2,
        barRadius: 10,
        cursorColor: '#ef860c',
      })

      // Loudness normalization and volume; CORS must be set before the source loads
      const media = this.displayWaveSurfer.getMediaElement()
      const demo = this.audioSource?.source === 'cdn' ? this.audioSource : null
      attachMedia(media, demo, prepareMedia(media, demo), this.audioPlayerStore.volume)

      if (hasPeaks) {
        this.duration = formatTime(duration)
        this.displayWaveSurfer.load(this.audioSrc, peaks, duration).catch(() => null)
      } else {
        this.displayWaveSurfer.load(this.audioSrc).catch(() => null)
      }

      const waveSurfer = this.displayWaveSurfer
      waveSurfer.on('error', (error) => {
        // destroy() aborts a running load and the old instance keeps its listeners
        if (this.displayWaveSurfer !== waveSurfer || error?.name === 'AbortError') {
          return
        }
        console.error('[AudioPlayer] WaveSurfer error:', error)
        this.audioPlayerStore.audioErrorMessage = 'Audio file unavailable. Please try again later.'
      })

      this.displayWaveSurfer.on('finish', () => {
        this.audioPlayerStore.isPlaying = false
        // Continue with the next demo, but stop after the last one
        if (this.hasPlaylist && this.demoIndex < this.demoCount - 1) {
          this.audioPlayerStore.selectDemo(this.demoIndex + 1, true)
        }
      })

      this.displayWaveSurfer.on('ready', () => {
        /* Working Cantrip for Debugging the Wavesurfer Ready Event */
        /**
         console.log('XY - WAVESURFER READY:', {
         shouldPlay: this.audioPlayerStore.shouldPlay,
         isPlaying: this.audioPlayerStore.isPlaying,
         triggeredByScroll: this.audioPlayerStore.triggeredByScroll,
         userClickedPlay: this.audioPlayerStore.userClickedPlay
         });
         **/
        this.waveIsReady = true
        this.audioPlayerStore.setLoadedWaveSurfer(this.displayWaveSurfer)
        if (this.audioPlayerStore.shouldPlay) {
          this.audioPlayerStore.togglePlay(true)
        }
      })

      if (this.showTrackLength) {
        this.displayWaveSurfer.on('decode', (duration) => {
          this.duration = formatTime(duration)
        })

        this.displayWaveSurfer.on('timeupdate', (currentTime) => {
          this.playbackTime = formatTime(currentTime)
        })
      }
    },
    togglePlay(setPlaying = false) {
      if (!this.displayWaveSurfer) {
        this.audioPlayerStore.loadFooterAudio(this.productDetails, true)
        this.fetchTrack()
        return
      }

      if (!this.loadedWaveSurfer || (this.loadedWaveSurfer.media.src !== this.displayWaveSurfer.media.src)) {
        this.audioPlayerStore.loadFooterAudio(this.productDetails, true)
        this.audioPlayerStore.setLoadedWaveSurfer(this.displayWaveSurfer)

        if (this.waveIsReady) {
          this.audioPlayerStore.togglePlay(true)
        } else {
          this.displayWaveSurfer.on('ready', () => this.audioPlayerStore.togglePlay(true))
        }
        return
      }

      this.audioPlayerStore.togglePlay(setPlaying)
    }
  }
}
</script>

<style lang="scss">
@use "~/common/scss/variables";
@use "~/common/scss/helpers";
@use "~/common/scss/mq";

.audio-player {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 1rem;

  &__waveform {
    width: 100%;
    height: 42px;
    display: block;
    visibility: hidden;
    &.is-ready {
      visibility: visible;
    }
  }

  @include mq.mobileOnly() {
    &__waveform {
      display: none;
    }
  }

  &__controls {
    display: flex;
    align-items: center;
    gap: .25rem;
  }

  &__play-button {
    cursor: pointer;
  }

  &__skip {
    align-items: center;
    color: currentColor;
    cursor: pointer;
    display: flex;
    height: helpers.px-to-rem(28);
    justify-content: center;
    padding: .25rem;
    width: helpers.px-to-rem(28);

    svg {
      height: 100%;
      width: 100%;
    }
  }

  // Internal marker: orange play triangle when the demo comes from our CDN (not on tiles)
  &__play-button.is-cdn .product-play__icon--play path {
    fill: #ef860c;
  }

  // Track number stacked above the play time
  &__time {
    display: flex;
    flex-direction: column;
    align-items: center;
    // Fixed width: the font has proportional digits, and a time box that changes width every second
    // resizes the waveform, which WaveSurfer then redraws with differently sampled bars
    flex-shrink: 0;
    width: 7.5rem;
    line-height: 1.2;
    text-align: center;
    white-space: nowrap;
  }

  &__volume {
    align-items: center;
    display: flex;
    flex-shrink: 0;
    gap: .25rem;

    svg {
      height: helpers.px-to-rem(20);
      width: helpers.px-to-rem(20);
    }

    input {
      accent-color: #ef860c;
      cursor: pointer;
      width: helpers.px-to-rem(80);
    }

    // Phones have hardware volume buttons, and iOS ignores media volume anyway
    @include mq.mobileOnly() {
      display: none;
    }
  }

  &__error {
    color: #e6421a;
    font-size: helpers.px-to-rem(18);
    width: variables.$containerSmall;
    text-align: center;

    @include mq.tabletOnly() {
      width: variables.$containerSmall;
      font-size: helpers.px-to-rem(16);
    }

    @include mq.mobileOnly() {
      width: 100%;
      margin: 0 0 0 1.5rem;
      font-size: helpers.px-to-rem(14);
    }
  }
}
</style>
